import { AppContext } from '../app/context';
import { ToolPointer } from './tool';
import { Vec3, dot, sub } from '../core/vec';
import { faceUsesEdge } from '../core/model';
import { closestPointOnRaySegment } from '../inference/inference';

/** Pixel-Toleranz, innerhalb der eine Kante unter dem Cursor gilt. */
const EDGE_PICK_PX = 8;

export interface PickResult {
  edgeId: number | null;
  faceId: number | null;
}

/**
 * Element unter dem Cursor wie SketchUp: nur Sichtbares ist wählbar.
 * Kanten (≤ 8 px) haben Vorrang vor Flächen, aber nur, wenn sie nicht hinter
 * der vordersten Fläche liegen — sonst würde ein Objekt dahinter gewählt.
 */
export function pickAt(ctx: AppContext, e: ToolPointer): PickResult {
  const hit = ctx.renderer.pickFaceHit(e.ndcX, e.ndcY, ctx.camera.camera);
  const front = hit?.distance ?? Infinity;
  const frontFace = hit ? ctx.model.faces.get(hit.faceId) : undefined;
  const model = ctx.model;

  let bestEdge: number | null = null;
  let bestDist = Infinity;
  for (const edge of model.edges.values()) {
    // Ausgeblendete Ebenen sind nicht wählbar
    if (!model.isEntityVisible(edge.group)) continue;
    const [a, b] = model.edgePoints(edge);
    const d = screenDistanceToSegment(ctx, a, b, e.x, e.y);
    if (d > EDGE_PICK_PX || d >= bestDist) continue;
    // Randkanten der getroffenen Fläche sind immer sichtbar
    const onFront = frontFace !== undefined && faceUsesEdge(frontFace, edge.a, edge.b);
    if (!onFront && !isVisible(e.ray, closestPointOnRaySegment(e.ray, a, b)?.point ?? a, front)) continue;
    bestDist = d;
    bestEdge = edge.id;
  }
  if (bestEdge !== null) return { edgeId: bestEdge, faceId: null };
  return { edgeId: null, faceId: hit?.faceId ?? null };
}

/** Liegt `p` (entlang des Blickstrahls) nicht hinter der vordersten Fläche? */
export function isVisible(ray: { origin: Vec3; dir: Vec3 }, p: Vec3, frontDistance: number): boolean {
  if (!Number.isFinite(frontDistance)) return true;
  const t = dot(sub(p, ray.origin), ray.dir);
  return t <= frontDistance + Math.max(1e-4, frontDistance * 2e-3);
}

/** Abstand des Cursors (px) zur Bildschirmprojektion der Strecke a–b. */
function screenDistanceToSegment(ctx: AppContext, a: Vec3, b: Vec3, x: number, y: number): number {
  const sa = ctx.camera.projectToScreen(a);
  const sb = ctx.camera.projectToScreen(b);
  const abx = sb.x - sa.x, aby = sb.y - sa.y;
  const lenSq = abx * abx + aby * aby;
  let t = lenSq > 0 ? ((x - sa.x) * abx + (y - sa.y) * aby) / lenSq : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(x - (sa.x + abx * t), y - (sa.y + aby * t));
}
